import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Theme, PREDEFINED_THEMES, CompanyTheme } from '../model/theme.model';
import { ThemeApiService, ThemeApiData, ThemeApiRequest } from './theme-api.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentTheme$ = new BehaviorSubject<Theme>(PREDEFINED_THEMES[0]);
  private companyThemes: Map<number, string> = new Map();
  private useBackendApi = true; // Flag to enable/disable API integration

  constructor(private themeApiService: ThemeApiService) {
    this.loadThemeFromStorage();
  }

  /**
   * Get current theme as observable
   */
  getCurrentTheme(): Observable<Theme> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get current theme value
   */
  getCurrentThemeValue(): Theme {
    return this.currentTheme$.value;
  }

  /**
   * Get all available themes
   */
  getAvailableThemes(): Theme[] {
    return PREDEFINED_THEMES;
  }

  /**
   * Apply theme by ID
   */
  applyTheme(themeId: string): void {
    const theme = PREDEFINED_THEMES.find(t => t.id === themeId);
    if (theme) {
      this.setTheme(theme);
    }
  }

  /**
   * Apply theme for specific company/institute
   * Loads from backend API if available, otherwise uses localStorage
   */
  applyCompanyTheme(companyId: number, instituteId: number): void {
    console.log('=========================================');
    console.log('🎨 applyCompanyTheme called');
    console.log('Company ID:', companyId);
    console.log('Institute ID:', instituteId);
    console.log('API enabled:', this.useBackendApi);
    console.log('=========================================');
    
    if (this.useBackendApi) {
      // Load theme from backend API
      console.log('🔄 Starting API theme load for company:', companyId);
      this.loadCompanyThemeFromApi(companyId).subscribe({
        next: (theme) => {
          console.log('✅ API theme load completed');
          if (theme) {
            console.log('🎨 Theme found in API:', theme.id, theme.name);
            this.setTheme(theme);
          } else {
            // No theme found in API, use default
            console.log('⚠️ No theme found in API, using default lavender');
            this.applyTheme('lavender');
          }
        },
        error: (error) => {
          console.error('❌ Failed to load theme from API:', error);
          console.log('📦 Falling back to localStorage');
          this.applyCompanyThemeFromStorage(companyId, instituteId);
        }
      });
    } else {
      // Use localStorage
      console.log('📦 Using localStorage for theme');
      this.applyCompanyThemeFromStorage(companyId, instituteId);
    }
  }

  /**
   * Apply theme from localStorage (fallback)
   */
  private applyCompanyThemeFromStorage(companyId: number, instituteId: number): void {
    const themeId = this.getCompanyThemeId(companyId, instituteId);
    if (themeId) {
      this.applyTheme(themeId);
    } else {
      this.applyTheme('lavender');
    }
  }

  /**
   * Load company theme from backend API
   */
  private loadCompanyThemeFromApi(companyId: number): Observable<Theme | null> {
    console.log('📡 Making API call: GET /theme-settings?company_id=' + companyId);
    return this.themeApiService.getThemeForCompany(companyId).pipe(
      tap(() => console.log('📨 HTTP request sent')),
      map(response => {
        console.log('📥 API response received:', JSON.stringify(response, null, 2));
        if (response.success && response.data) {
          console.log('✅ Response has data, converting to theme object');
          const theme = this.convertApiDataToTheme(response.data);
          console.log('🎨 Converted theme:', JSON.stringify(theme, null, 2));
          return theme;
        }
        console.log('⚠️ No theme data in response');
        return null;
      }),
      catchError(error => {
        console.error('❌ Error loading theme from API:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        return of(null);
      })
    );
  }

  /**
   * Convert API data to Theme object
   */
  private convertApiDataToTheme(apiData: ThemeApiData): Theme {
    // Check if this matches a predefined theme
    const predefinedTheme = PREDEFINED_THEMES.find(t => t.id === apiData.theme_id);
    
    if (predefinedTheme) {
      return predefinedTheme;
    }

    // Create custom theme from API data
    return {
      id: apiData.theme_id || `custom-${apiData.company_id}`,
      name: `Custom Theme`,
      colors: {
        primary: apiData.colors.primary,
        primaryLight: apiData.colors.primaryLight || apiData.colors.primary,
        primaryDark: apiData.colors.primaryDark || apiData.colors.primary,
        secondary: apiData.colors.secondary || apiData.colors.primary,
        secondaryLight: apiData.colors.secondaryLight || apiData.colors.primary,
        secondaryDark: apiData.colors.secondaryDark || apiData.colors.primary
      },
      gradient: apiData.gradients?.primary || `linear-gradient(135deg, ${apiData.colors.primary} 0%, ${apiData.colors.secondary || apiData.colors.primary} 100%)`,
      gradientHover: apiData.gradients?.hover || apiData.gradients?.primary || '',
      gradientLight: apiData.gradients?.light || '',
      shadow: apiData.shadows?.primary || `0 8px 24px ${apiData.colors.primary}66`
    };
  }

  /**
   * Save company theme preference
   * Saves to backend API if available, otherwise localStorage
   */
  saveCompanyTheme(companyId: number, instituteId: number, themeId: string): void {
    const theme = PREDEFINED_THEMES.find(t => t.id === themeId);
    
    if (!theme) {
      console.error('Theme not found:', themeId);
      return;
    }

    if (this.useBackendApi) {
      // Save to backend API
      this.saveCompanyThemeToApi(companyId, theme).subscribe({
        next: () => {
          console.log('Theme saved to backend successfully');
          this.applyTheme(themeId);
          // Also save to localStorage as backup
          this.saveCompanyThemeToStorage(companyId, instituteId, themeId);
        },
        error: (error) => {
          console.error('Failed to save theme to API, saving to localStorage only:', error);
          this.saveCompanyThemeToStorage(companyId, instituteId, themeId);
          this.applyTheme(themeId);
        }
      });
    } else {
      // Save to localStorage
      this.saveCompanyThemeToStorage(companyId, instituteId, themeId);
      this.applyTheme(themeId);
    }
  }

  /**
   * Save company theme to localStorage
   */
  private saveCompanyThemeToStorage(companyId: number, instituteId: number, themeId: string): void {
    this.companyThemes.set(companyId, themeId);
    
    const companyThemesObj: any = {};
    this.companyThemes.forEach((value, key) => {
      companyThemesObj[key] = value;
    });
    localStorage.setItem('companyThemes', JSON.stringify(companyThemesObj));
  }

  /**
   * Save company theme to backend API
   */
  private saveCompanyThemeToApi(companyId: number, theme: Theme): Observable<any> {
    const apiRequest: ThemeApiRequest = {
      company_id: companyId,
      theme_id: theme.id,
      colors: {
        primary: theme.colors.primary,
        primaryLight: theme.colors.primaryLight,
        primaryDark: theme.colors.primaryDark,
        secondary: theme.colors.secondary,
        secondaryLight: theme.colors.secondaryLight,
        secondaryDark: theme.colors.secondaryDark
      },
      gradients: {
        primary: theme.gradient,
        hover: theme.gradientHover,
        light: theme.gradientLight
      },
      shadows: {
        primary: theme.shadow
      },
      is_active: true
    };

    // Check if theme already exists for this company
    return this.themeApiService.getThemeForCompany(companyId).pipe(
      catchError(() => of({ success: false, message: '', data: null })),
      tap(response => {
        if (response.success && response.data) {
          // Theme exists, update it
          this.themeApiService.updateTheme(companyId, apiRequest).subscribe({
            next: () => console.log('Theme updated successfully'),
            error: (error) => console.error('Error updating theme:', error)
          });
        } else {
          // Theme doesn't exist, create it
          this.themeApiService.createTheme(apiRequest).subscribe({
            next: () => console.log('Theme created successfully'),
            error: (error) => console.error('Error creating theme:', error)
          });
        }
      }),
      map(() => true)
    );
  }

  /**
   * Get company theme ID
   */
  getCompanyThemeId(companyId: number, instituteId: number): string | undefined {
    return this.companyThemes.get(companyId);
  }

  /**
   * Set and apply theme
   */
  private setTheme(theme: Theme): void {
    this.currentTheme$.next(theme);
    this.applyCSSVariables(theme);
    this.saveThemeToStorage(theme.id);
  }

  /**
   * Apply CSS custom properties to document root
   */
  private applyCSSVariables(theme: Theme): void {
    const root = document.documentElement;

    // Apply color variables
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-primary-light', theme.colors.primaryLight);
    root.style.setProperty('--color-primary-dark', theme.colors.primaryDark);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-secondary-light', theme.colors.secondaryLight);
    root.style.setProperty('--color-secondary-dark', theme.colors.secondaryDark);

    // Apply gradient variables
    root.style.setProperty('--gradient-primary', theme.gradient);
    root.style.setProperty('--gradient-primary-hover', theme.gradientHover);
    root.style.setProperty('--gradient-primary-light', theme.gradientLight);
    
    // Apply shadow
    root.style.setProperty('--shadow-primary', theme.shadow);
    root.style.setProperty('--shadow-primary-lg', theme.shadow.replace('2px 8px', '4px 12px').replace('0.3', '0.4'));
    root.style.setProperty('--shadow-primary-sm', theme.shadow.replace('2px 8px', '2px 6px').replace('0.3', '0.25'));
  }

  /**
   * Load theme from localStorage
   */
  private loadThemeFromStorage(): void {
    try {
      // Load company themes
      const companyThemesStr = localStorage.getItem('companyThemes');
      if (companyThemesStr) {
        const companyThemesObj = JSON.parse(companyThemesStr);
        Object.keys(companyThemesObj).forEach(key => {
          this.companyThemes.set(Number(key), companyThemesObj[key]);
        });
      }

      // Load current theme
      const themeId = localStorage.getItem('currentTheme');
      if (themeId) {
        this.applyTheme(themeId);
      } else {
        // Apply default theme
        this.setTheme(PREDEFINED_THEMES[0]);
      }
    } catch (error) {
      console.error('Error loading theme from storage:', error);
      this.setTheme(PREDEFINED_THEMES[0]);
    }
  }

  /**
   * Save current theme to localStorage
   */
  private saveThemeToStorage(themeId: string): void {
    try {
      localStorage.setItem('currentTheme', themeId);
    } catch (error) {
      console.error('Error saving theme to storage:', error);
    }
  }

  /**
   * Reset to default theme
   */
  resetToDefault(): void {
    this.applyTheme('lavender');
  }
}
